import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { SpecialtyProvider } from '@/contexts/SpecialtyContext';

import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { LogOut, FlaskConical } from 'lucide-react';
import { DemoPathToggle } from '@/components/ui/DemoPathToggle';
import { toast } from 'sonner';

function getGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

interface SurgeonLayoutProps {
  children: ReactNode;
}

export function SurgeonLayout({ children }: SurgeonLayoutProps) {
  const navigate = useNavigate();
  const { signOut, profile, role } = useAuthContext();
  const { isDemoMode, toggleDemoMode } = useDemoMode();

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
    toast.success('Securely Signed Out', {
      description: 'Your session has been terminated and all cached data cleared.',
    });
  };

  return (
    <SpecialtyProvider>
      <div className="min-h-screen bg-background flex flex-col">
        {/* Terminal Header */}
        <header className="sticky top-0 z-50 border-b border-border bg-card/80 backdrop-blur-md">
          <div className="flex items-center justify-between h-14 px-4 lg:px-6">
            <button onClick={() => navigate('/')} className="shrink-0">
              <span className="text-lg font-extrabold tracking-tight text-foreground font-mono">LINK</span>
            </button>

            <div className="flex items-center gap-2 sm:gap-3">
              {/* Demo Path Toggle (only in demo mode) */}
              <DemoPathToggle />

              {/* Admin Demo Mode Toggle */}
              {role === 'admin' && (
                <div className="hidden sm:flex items-center gap-2 px-3 py-1 rounded-lg border bg-card/50">
                  <FlaskConical className="w-4 h-4 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Demo</span>
                  <Switch
                    checked={isDemoMode}
                    onCheckedChange={toggleDemoMode}
                    className="scale-75"
                  />
                </div>
              )}

              <div className="hidden sm:flex items-center gap-1.5 text-sm text-muted-foreground">
                <span>{getGreeting()},</span>
                <span className="font-medium text-foreground max-w-[180px] truncate">
                  {profile?.full_name
                    ? profile.full_name.startsWith('Dr.') ? profile.full_name : `Dr. ${profile.full_name.split(' ').pop()}`
                    : 'Doctor'}
                </span>
              </div>
              <Button variant="ghost" size="sm" onClick={handleSignOut}>
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline ml-2">Sign Out</span>
              </Button>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-auto">
          <div className="page-shell-main-compact">{children}</div>
        </main>
      </div>
    </SpecialtyProvider>
  );
}
